import React, { Component } from 'react';
import Modal from '../../components/Modal';
import { getAuthHeaders, urlMap } from '../../../utils';

/**
 * Generic modal that fetches + displays the results of a chapter-stage agent step.
 *
 * Props:
 *  - isOpen        : boolean
 *  - onClose       : () => void
 *  - chapter       : { id, title, order_no }
 *  - projectId     : string
 *  - stepName      : string   (e.g. "DetectShowVsTellStep")
 *  - stepLabel     : string   (e.g. "Show vs Tell")
 */
export default class StepResultsModal extends Component {
  constructor(props) {
    super(props);
    this.state = { statusMap: StepResultsModal.buildStatusMap(props.metadata) };
  }

  static buildStatusMap(metadata) {
    const statusMap = {};
    if (!metadata) return statusMap;
    for (const key of ['showVsTell', 'pacing', 'holes']) {
      (metadata[key]?.issues || []).forEach((item) => {
        if (item.id) statusMap[item.id] = item.status || 'unresolved';
      });
    }
    return statusMap;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.metadata !== this.props.metadata) {
      this.setState({ statusMap: StepResultsModal.buildStatusMap(this.props.metadata) });
    }
  }

  getStatus(id) {
    return this.state.statusMap[id] || 'unresolved';
  }

  async updateStatus(id, status) {
    const chapterId = this.props.chapter?.id;
    if (!chapterId || !id) return;

    this.setState((prev) => ({
      statusMap: { ...prev.statusMap, [id]: status },
    }));

    try {
      await fetch(urlMap.updateMetadataItemStatus(chapterId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id, status }),
      });
    } catch {
      const revert = status === 'solved' ? 'unresolved' : 'solved';
      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [id]: revert },
      }));
    }
  }

  markSolved(id) {
    const current = this.getStatus(id);
    if (current === 'verified') return;
    const next = current === 'solved' ? 'unresolved' : 'solved';
    this.updateStatus(id, next);
  }

  renderStatusButton(id) {
    const status = this.getStatus(id);
    if (status === 'verified') {
      return <span className="step-result-status step-result-status--verified">✓✓ Verified</span>;
    }
    return (
      <button
        className={`step-result-status-btn ${status === 'solved' ? 'step-result-status-btn--solved' : ''}`}
        onClick={() => this.markSolved(id)}
      >
        {status === 'solved' ? '✓ Solved' : '✓'}
      </button>
    );
  }

  getHoleLevelLabel(item) {
    const stage = item?.agentStage
      || (item?.source && String(item.source).includes('project') ? 'project_stage' : 'chapter_stage');
    return stage === 'project_stage' ? 'Plot' : 'Chapter';
  }

  getResultsFromMetadata() {
    const { metadata, stepName } = this.props;
    if (!metadata) return null;
    switch (stepName) {
      case 'DetectShowVsTellStep':
        return metadata.showVsTell || null;
      case 'DetectPacingIssuesStep':
        return metadata.pacing || null;
      case 'ChapterHooksAndScenesStep':
        return {
          hooks: metadata.hooks || null,
          scenes: metadata.scenes || null,
        };
      case 'DetectChapterHolesStep':
        return metadata.holes || null;
      default:
        return null;
    }
  }

  /* ── renderers per step type ── */

  renderShowVsTell(results) {
    const issues = results?.issues || results?.items || (Array.isArray(results) ? results : []);
    if (!issues.length) return <p className="step-results-empty">No show-vs-tell issues found — nice work!</p>;

    return (
      <div className="step-results-list">
        {issues.map((item, i) => {
          const resolved = this.getStatus(item.id) !== 'unresolved';
          return (
            <div key={item.id || i} className={`step-result-card step-result-card--showvstell${resolved ? ' step-result-card--resolved' : ''}`}>
              <div className="step-result-card-header">
                <span className="step-result-badge step-result-badge--tell">Tell</span>
                <div className="step-result-card-header-right">
                  {item.severity && <span className={`step-result-severity step-result-severity--${item.severity}`}>{item.severity}</span>}
                  {this.renderStatusButton(item.id)}
                </div>
              </div>
              {item.passage && <blockquote className={`step-result-quote${resolved ? ' step-result-text--resolved' : ''}`}>{item.passage}</blockquote>}
              {item.explanation && <p className={`step-result-explanation${resolved ? ' step-result-text--resolved' : ''}`}>{item.explanation}</p>}
              {item.suggestion && (
                <div className={`step-result-suggestion${resolved ? ' step-result-text--resolved' : ''}`}>
                  <span className="step-result-suggestion-label">Suggestion:</span> {item.suggestion}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderPacingIssues(results) {
    const issues = results?.issues || results?.items || (Array.isArray(results) ? results : []);
    if (!issues.length) return <p className="step-results-empty">No pacing issues detected.</p>;

    return (
      <div className="step-results-list">
        {issues.map((item, i) => {
          const resolved = this.getStatus(item.id) !== 'unresolved';
          return (
            <div key={item.id || i} className={`step-result-card step-result-card--pacing${resolved ? ' step-result-card--resolved' : ''}`}>
              <div className="step-result-card-header">
                <span className={`step-result-badge step-result-badge--${item.type === 'rushing' ? 'rush' : 'drag'}`}> 
                  {item.type === 'rushing' ? '⚡ Rushing' : '🐌 Dragging'}
                </span>
                <div className="step-result-card-header-right">
                  {item.severity && <span className={`step-result-severity step-result-severity--${item.severity}`}>{item.severity}</span>}
                  {this.renderStatusButton(item.id)}
                </div>
              </div>
              {item.excerpt && <blockquote className={`step-result-quote${resolved ? ' step-result-text--resolved' : ''}`}>{item.excerpt}</blockquote>}
              {item.location && <p className={`step-result-section${resolved ? ' step-result-text--resolved' : ''}`}><strong>Location:</strong> {item.location}</p>}
              {item.explanation && <p className={`step-result-explanation${resolved ? ' step-result-text--resolved' : ''}`}>{item.explanation}</p>}
              {item.suggestion && (
                <div className={`step-result-suggestion${resolved ? ' step-result-text--resolved' : ''}`}>
                  <span className="step-result-suggestion-label">Suggestion:</span> {item.suggestion}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderChapterHooks(results) {
    const opening = results?.opening || results?.hook;
    const closing = results?.closing || results?.cliffhanger;

    return (
      <div className="step-results-hooks">
        {/* Opening Hook */}
        <div className="step-result-card step-result-card--hook">
          <div className="step-result-card-header">
            <span className="step-result-badge step-result-badge--hook">🪝 Opening Hook</span>
            {opening?.rating && (
              <span className={`step-result-rating step-result-rating--${opening.rating}`}>{opening.rating}</span>
            )}
          </div>
          {opening ? (
            <>
              {opening.passage && <blockquote className="step-result-quote">{opening.passage}</blockquote>}
              {opening.analysis && <p className="step-result-explanation">{opening.analysis}</p>}
              {opening.suggestion && (
                <div className="step-result-suggestion">
                  <span className="step-result-suggestion-label">Suggestion:</span> {opening.suggestion}
                </div>
              )}
            </>
          ) : (
            <p className="step-results-empty">No opening hook analysis available.</p>
          )}
        </div>

        {/* Closing Tension */}
        <div className="step-result-card step-result-card--hook">
          <div className="step-result-card-header">
            <span className="step-result-badge step-result-badge--tension">⚡ Closing Tension</span>
            {closing?.rating && (
              <span className={`step-result-rating step-result-rating--${closing.rating}`}>{closing.rating}</span>
            )}
          </div>
          {closing ? (
            <>
              {closing.passage && <blockquote className="step-result-quote">{closing.passage}</blockquote>}
              {closing.analysis && <p className="step-result-explanation">{closing.analysis}</p>}
              {closing.suggestion && (
                <div className="step-result-suggestion">
                  <span className="step-result-suggestion-label">Suggestion:</span> {closing.suggestion}
                </div>
              )}
            </>
          ) : (
            <p className="step-results-empty">No closing tension analysis available.</p>
          )}
        </div>
      </div>
    );
  }

  renderHooksAndScenes(results) {
    const hooks = results?.hooks || {};
    const scenes = results?.scenes || {};
    const scenesList = scenes?.scenes || [];

    return (
      <div className="step-results-hooks-and-scenes">
        {/* Hooks Section */}
        <h3 className="step-results-section-title">Hooks</h3>
        <div className="step-results-hooks">
          {/* Opening Hook */}
          <div className="step-result-card step-result-card--hook">
            <div className="step-result-card-header">
              <span className="step-result-badge step-result-badge--hook">🪝 Opening Hook</span>
              {hooks.opening?.rating && (
                <span className={`step-result-rating step-result-rating--${hooks.opening.rating}`}>{hooks.opening.rating}</span>
              )}
            </div>
            {hooks.opening ? (
              <>
                {hooks.opening.passage && <blockquote className="step-result-quote">{hooks.opening.passage}</blockquote>}
                {hooks.opening.analysis && <p className="step-result-explanation">{hooks.opening.analysis}</p>}
                {hooks.opening.suggestion && (
                  <div className="step-result-suggestion">
                    <span className="step-result-suggestion-label">Suggestion:</span> {hooks.opening.suggestion}
                  </div>
                )}
              </>
            ) : (
              <p className="step-results-empty">No opening hook analysis available.</p>
            )}
          </div>

          {/* Closing Tension */}
          <div className="step-result-card step-result-card--hook">
            <div className="step-result-card-header">
              <span className="step-result-badge step-result-badge--tension">⚡ Closing Tension</span>
              {hooks.closing?.rating && (
                <span className={`step-result-rating step-result-rating--${hooks.closing.rating}`}>{hooks.closing.rating}</span>
              )}
            </div>
            {hooks.closing ? (
              <>
                {hooks.closing.passage && <blockquote className="step-result-quote">{hooks.closing.passage}</blockquote>}
                {hooks.closing.analysis && <p className="step-result-explanation">{hooks.closing.analysis}</p>}
                {hooks.closing.suggestion && (
                  <div className="step-result-suggestion">
                    <span className="step-result-suggestion-label">Suggestion:</span> {hooks.closing.suggestion}
                  </div>
                )}
              </>
            ) : (
              <p className="step-results-empty">No closing tension analysis available.</p>
            )}
          </div>
        </div>

        {hooks.summary && <p className="step-results-summary">{hooks.summary}</p>}

        {/* Scenes Section */}
        <h3 className="step-results-section-title" style={{ marginTop: '1.5rem' }}>Scenes ({scenes.totalScenes || scenesList.length})</h3>
        {scenesList.length === 0 ? (
          <p className="step-results-empty">No scenes extracted.</p>
        ) : (
          <div className="step-results-list">
            {scenesList.map((scene, i) => (
              <div key={i} className="step-result-card step-result-card--scene">
                <div className="step-result-card-header">
                  <span className="step-result-badge step-result-badge--scene">Scene {i + 1}</span>
                  {scene.type && <span className="step-result-badge">{scene.type}</span>}
                </div>
                {scene.title && <h4 className="step-result-scene-title">{scene.title}</h4>}
                {scene.summary && <p className="step-result-explanation">{scene.summary}</p>}
                {scene.characters && scene.characters.length > 0 && (
                  <p className="step-result-meta"><strong>Characters:</strong> {scene.characters.join(', ')}</p>
                )}
                {scene.setting && <p className="step-result-meta"><strong>Setting:</strong> {scene.setting}</p>}
                {scene.purpose && <p className="step-result-meta"><strong>Purpose:</strong> {scene.purpose}</p>}
              </div>
            ))}
          </div>
        )}

        {scenes.summary && <p className="step-results-summary" style={{ marginTop: '0.5rem' }}>{scenes.summary}</p>}
      </div>
    );
  }

  renderChapterHoles(results) {
    const issues = results?.issues || (Array.isArray(results) ? results : []);
    if (!issues.length) return <p className="step-results-empty">No inconsistencies detected — chapter looks solid!</p>;

    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...issues].map((item, origIdx) => ({ item, origIdx })).sort((a, b) => (severityOrder[a.item.severity] ?? 9) - (severityOrder[b.item.severity] ?? 9));

    return (
      <div className="step-results-list step-results-list--holes">
        {sorted.map(({ item, origIdx }) => {
          const resolved = this.getStatus(item.id) !== 'unresolved';
          return (
            <div key={item.id || origIdx} className={`step-result-card step-result-card--hole${resolved ? ' step-result-card--resolved' : ''}`}>
              <div className="step-result-card-header">
                <span className={`step-result-badge step-result-badge--${item.type || 'generic'}`}>
                  {item.type ? item.type.replace(/_/g, ' ') : 'Issue'}
                </span>
                <span className="step-result-level-badge">{this.getHoleLevelLabel(item)}</span>
                <div className="step-result-card-header-right">
                  {item.severity && (
                    <span className={`step-result-severity step-result-severity--${item.severity}`}>{item.severity}</span>
                  )}
                  {this.renderStatusButton(item.id)}
                </div>
              </div>
              {item.description && <p className={`step-result-explanation${resolved ? ' step-result-text--resolved' : ''}`}>{item.description}</p>}
              {item.evidence && <blockquote className={`step-result-quote${resolved ? ' step-result-text--resolved' : ''}`}>{item.evidence}</blockquote>}
              {item.suggestion && (
                <div className={`step-result-suggestion${resolved ? ' step-result-text--resolved' : ''}`}>
                  <span className="step-result-suggestion-label">Suggestion:</span> {item.suggestion}
                </div>
              )}
            </div>
          );
        })}
        {results?.summary && <p className="step-results-summary" style={{ marginTop: '1rem' }}>{results.summary}</p>}
      </div>
    );
  }

  renderResults() {
    const { stepName } = this.props;
    const results = this.getResultsFromMetadata();

    if (!results || (stepName === 'ChapterHooksAndScenesStep' && !results.hooks && !results.scenes)) {
      return (
        <div className="step-results-empty-state">
          <p>No results yet.</p>
          <p className="step-results-empty-hint">
            Run the chapter agent to generate analysis for this step.
          </p>
        </div>
      );
    }

    switch (stepName) {
      case 'DetectShowVsTellStep':
        return this.renderShowVsTell(results);
      case 'DetectPacingIssuesStep':
        return this.renderPacingIssues(results);
      case 'ChapterHooksAndScenesStep':
        return this.renderHooksAndScenes(results);
      case 'DetectChapterHolesStep':
        return this.renderChapterHoles(results);
      default:
        return <pre className="step-results-raw">{JSON.stringify(results, null, 2)}</pre>;
    }
  }

  render() {
    const { isOpen, onClose, stepLabel, order_no, title } = this.props;
    if (!isOpen) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="large">
        <div className="step-results-modal">
          <div className="step-results-modal-header">
            <h2 className="step-results-modal-title">
              {stepLabel} — Chapter {order_no}: {title}
            </h2>
          </div>
          <div className="step-results-modal-body">
            {this.renderResults()}
          </div>
          <div className="step-results-modal-footer">
            <button className="step-results-modal-close" onClick={onClose}>Close</button>
          </div>
        </div>
      </Modal>
    );
  }
}
